import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { Fragment, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useIsMobile } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';

import { disabledAreaColor, getAreaColor } from './areaColors';

type Props = {
  className?: string;
  rows: {
    areaName: string;
    isDisabledArea: boolean;
    deliveryFee: ReactNode;
    minBasketSize: ReactNode;
    enabled: ReactNode;
  }[];
  hideEnabledControl?: boolean;
};

const Root = styled.div`
  overflow-x: auto;
  padding-block-end: 12px;
`;

const Table = styled.div<{ hideLastColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${({ hideLastColumn }) =>
    `minmax(120px, 3fr) repeat(2, minmax(min-content, 1fr))${hideLastColumn ? '' : ' max-content'};`};
  gap: 12px 24px;
`;

const HeaderCell = styled.div`
  ${typographyCss.Small}
  color: ${cbSemanticCssVars.textSecondary};
  text-transform: uppercase;
`;
const Heading = styled(HeaderCell)`
  margin-block-end: 8px;
`;

const Cell = styled.div`
  ${typographyCss.Body3}
`;

const Row = styled.div<{ hideLastColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${({ hideLastColumn }) => `1fr 1fr ${hideLastColumn ? '' : 'minmax(auto, min-content)'}`};
  gap: 16px;
  padding-block-end: 16px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  margin-block-end: 16px;
`;

const TitleCell = styled(Cell)`
  grid-column: 1 / 3;
`;
const AreaTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;
const AreaColor = styled.div<{ color: string; disabled: boolean }>`
  height: 12px;
  width: 12px;
  flex: 0 0 12px;
  background-color: ${({ color, disabled, theme }) => (disabled ? disabledAreaColor[theme.cbThemeMode] : color)};
  opacity: 0.64;
  border-radius: 12px;
  margin-block-start: 4px;
`;

const AreaName = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FeeCell = styled(Cell)`
  grid-column: 1 / 2;
`;
const MovCell = styled(Cell)`
  grid-column: 2 / 4;
`;
const EnabledCell = styled(Cell)`
  grid-column: 3 / 4;
`;

const DeliveryAreasTable = ({ className, rows, hideEnabledControl }: Props) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className={className}>
        {rows.map(({ areaName, deliveryFee, minBasketSize, enabled, isDisabledArea }, index) => (
          <Row key={`${areaName}-${index}`} hideLastColumn={hideEnabledControl}>
            <TitleCell>
              <Heading>
                <FormattedMessage id="venue-details.delivery-areas.area-name" />
              </Heading>
              <AreaTitle>
                <AreaColor color={getAreaColor(index)} disabled={isDisabledArea} />
                <AreaName dir="auto">{areaName}</AreaName>
              </AreaTitle>
            </TitleCell>
            {!hideEnabledControl && (
              <EnabledCell>
                <Heading>
                  <FormattedMessage id="venue-details.delivery-areas.enabled" />
                </Heading>
                {enabled}
              </EnabledCell>
            )}
            <FeeCell>
              <Heading>
                <FormattedMessage id="venue-details.delivery-areas.delivery-fee" />
              </Heading>
              {deliveryFee}
            </FeeCell>
            <MovCell>
              <Heading>
                <FormattedMessage id="venue-details.delivery-areas.min-basket-size" />
              </Heading>
              {minBasketSize}
            </MovCell>
          </Row>
        ))}
      </div>
    );
  }

  return (
    <Root className={className}>
      <Table hideLastColumn={hideEnabledControl}>
        <HeaderCell>
          <FormattedMessage id="venue-details.delivery-areas.area-name" />
        </HeaderCell>
        <HeaderCell>
          <FormattedMessage id="venue-details.delivery-areas.delivery-fee" />
        </HeaderCell>
        <HeaderCell>
          <FormattedMessage id="venue-details.delivery-areas.min-basket-size" />
        </HeaderCell>
        {!hideEnabledControl && (
          <HeaderCell>
            <FormattedMessage id="venue-details.delivery-areas.enabled" />
          </HeaderCell>
        )}

        {rows.map(({ areaName, deliveryFee, minBasketSize, enabled, isDisabledArea }, index) => (
          <Fragment key={`${areaName}-${index}`}>
            <Cell>
              <AreaTitle>
                <AreaColor color={getAreaColor(index)} disabled={isDisabledArea} />
                <AreaName dir="auto">{areaName}</AreaName>
              </AreaTitle>
            </Cell>
            <Cell>{deliveryFee}</Cell>
            <Cell>{minBasketSize}</Cell>
            {!hideEnabledControl && <Cell>{enabled}</Cell>}
          </Fragment>
        ))}
      </Table>
    </Root>
  );
};

export default DeliveryAreasTable;
